import {Box} from '@mui/material';
import {useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import {useEffect, useState} from 'react';
import EditWorkingTimeDialog from './projectDetails/EditWorkingTimeDialog';
import {DateTime} from 'luxon';
import {WorkingTime} from '../../state/types/WorkingTime';
import TrackTimeDialog from './customerList/TrackTimeDialog';
import WorkingTimeFilterBar from './projectDetails/WorkingTimeFilterBar';
import ProjectDetailTable from './projectDetails/ProjectDetailTable';
import ArchiveProjectDialog from './customerList/ArchiveProjectDialog';
import DeleteWorkingTimeDialog from './projectDetails/DeleteWorkingTimeDialog';
import Command from '../../state/actions/command';
import {useDispatch} from 'react-redux';
import {downloadCsvFile, generateTimeTrackingCsv} from '../../util/csvExport';
import {endOfFilterDay, isWithinRange, rangeInDays, startOfFilterDay} from '../../util/workingTimeFilter';

const ProjectDetailsPage = () => {
    const dispatch = useDispatch();

    const params = useParams<{ customerId: string, projectId: string }>();
    const customer = useSelector((state: any) => Selector.Customer.customer(state, params.customerId || ''));
    const project = useSelector((state: any) => Selector.Customer.project(state, params.projectId || ''));
    const workingTimeList = useSelector(
        (state: any) => Selector.WorkingTime.workingTimeList(state, params.projectId || ''),
    );
    const [filteredWorkingTimeList, setFilteredWorkingTimeList] = useState<WorkingTime[]>([]);
    const [workingTimeToEdit, setWorkingTimeToEdit] = useState<string>();
    const [editWorkingTimeDialogOpen, setEditWorkingTimeDialogOpen] = useState<boolean>(false);
    const [trackTimeDialogOpen, setTrackTimeDialogOpen] = useState<boolean>(false);
    const [archiveProjectDialogOpen, setArchiveProjectDialogOpen] = useState<boolean>(false);
    const [deleteWorkingTimeDialogOpen, setDeleteWorkingTimeDialogOpen] = useState<boolean>(false);
    const [workingTimeToDelete, setWorkingTimeToDelete] = useState<string>();
    const [start, setStart] = useState<DateTime|null>(DateTime.now().startOf('month'));
    const [end, setEnd] = useState<DateTime|null>(DateTime.now().endOf('day'));
    const [filterIsSetFlag, setFilterIsSetFlag] = useState<boolean>(true);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

    const updateLocalData = () => {
        dispatch(Command.Customer.fetchCustomerList({}));
        dispatch(Command.WorkingTime.fetchAllWorkingTimeList({}));
    };

    const openEditWorkingTimeDialog = (workingTimeId: string) => {
        setWorkingTimeToEdit(workingTimeId);
        setEditWorkingTimeDialogOpen(true);
    };

    const closeEditWorkingTimeDialog = () => {
        setEditWorkingTimeDialogOpen(false);
        setWorkingTimeToEdit(undefined);
    };

    const openTrackTimeDialog = () => {
        setTrackTimeDialogOpen(true);
    };

    const closeTrackTimeDialog = () => {
        setTrackTimeDialogOpen(false);
    };

    const openArchiveProjectDialog = () => {
        setArchiveProjectDialogOpen(true);
    };

    const closeArchiveProjectDialog = () => {
        setArchiveProjectDialogOpen(false);
    };

    const openDeleteWorkingTimeDialog = (workingTimeId: string) => {
        setWorkingTimeToDelete(workingTimeId);
        setDeleteWorkingTimeDialogOpen(true);
    };

    const closeDeleteWorkingTimeDialog = () => {
        setDeleteWorkingTimeDialogOpen(false);
        setWorkingTimeToDelete(undefined);
    };

    const exportPdfForSelectedTime = () => {
        if (!project || !start || !end) {
            return;
        }

        const days = rangeInDays(start, end);
        if (days < 1 || days > 90) {
            dispatch(Command.Notification.enqueueNotification({
                severity: 'error',
                message: 'Die Zeitspanne des Exports muss zwischen 1 - 90 Tagen liegen.',
            }));
            return;
        }

        dispatch(Command.DateRangeExport.requestDateRangeExport({
            projectId: project.projectId,
            startParam: start.toISODate(),
            endParam: end.toISODate(),
            employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
        }));
    };

    const exportCsv = () => {
        if (!project || filteredWorkingTimeList.length === 0) {
            dispatch(Command.Notification.enqueueNotification({
                severity: 'error',
                message: 'Keine Daten zum Exportieren vorhanden.',
            }));
            return;
        }
        const csv = generateTimeTrackingCsv(filteredWorkingTimeList, project.projectId);
        downloadCsvFile(csv, `export_timetracker_${project.name}.csv`);
    };

    const resetFilterDetailPage = () => {
        setStart(DateTime.now().startOf('month'));
        setEnd(DateTime.now().endOf('day'));
        setFilterIsSetFlag(false);
        setSelectedEmployeeIds([]);
        project!.filterTime = project!.total;
        setFilteredWorkingTimeList(workingTimeList ? sortListStartDesc(workingTimeList) : []);
        updateLocalData();
    };

    const filterWorkingTime = () => {
        filterWorkingTimeList();
        updateLocalData();
    };

    const filterWorkingTimeList = () => {
        if (!workingTimeList || !project) {
            return;
        }
        const filteredList: WorkingTime[] = workingTimeList.filter(
            (workingTime) => {
                const dateMatch = isWithinRange(workingTime, start!, end!);
                const userMatch = selectedEmployeeIds.length === 0
                    || selectedEmployeeIds.includes(workingTime.employeeId);
                return dateMatch && userMatch;
            },
        );
        project.filterTime = filteredProjectTime();
        setFilteredWorkingTimeList(sortListStartDesc(filteredList));
        setFilterIsSetFlag(true);
    };

    const filteredProjectTime = (): number => {
        if (!start || !end || !workingTimeList) {
            return 0;
        }
        let filteredTime = 0;
        for (const workTime of workingTimeList) {
            const dateMatch = isWithinRange(workTime, start, end);
            const userMatch = selectedEmployeeIds.length === 0
                || selectedEmployeeIds.includes(workTime.employeeId);
            if (dateMatch && userMatch) {
                const timeDiff = ((workTime.end.getTime() - workTime.start.getTime()) / (60 * 60 * 1000));
                filteredTime += parseFloat(timeDiff.toFixed(2));
            }
        }
        return filteredTime;
    };

    const sortListStartDesc = (unsortedList: WorkingTime[]) => {
        return unsortedList.sort(
            (le1, le2) =>
                (le1.start < le2.start) ? 1 : (le1.start > le2.start) ? -1 : 0);
    };

    useEffect(() => {
        if (!filterIsSetFlag) {
            project!.filterTime = project!.total;
        }
        filterIsSetFlag ? filterWorkingTimeList() :
            setFilteredWorkingTimeList(workingTimeList ? sortListStartDesc(workingTimeList) : []);
    }, [workingTimeList, project, customer]);

    useEffect(() => {
        updateLocalData();
    }, []);

    if (!customer || !project) {
        return null;
    }

    const availableTags = workingTimeList
        ? Array.from(new Set(workingTimeList.flatMap(wt => wt.tags || []))).sort()
        : [];

    return (
        <Box>
            <WorkingTimeFilterBar
                start={start}
                end={end}
                onChangeStart={(newValue) => setStart(startOfFilterDay(newValue))}
                onChangeEnd={(newValue) => setEnd(endOfFilterDay(newValue))}
                onClickFilterList={filterWorkingTime}
                onClickResetFilter={resetFilterDetailPage}
                filterIsSet={filterIsSetFlag}
                selectedEmployeeIds={selectedEmployeeIds}
                onChangeEmployeeIds={setSelectedEmployeeIds}
                projectEmployees={project.employees}
            />
            <ProjectDetailTable
                customer={customer}
                project={project}
                workingTimeList={filteredWorkingTimeList}
                onClickTrackTimeDialog={openTrackTimeDialog}
                onClickEditWorkingTimeDialog={openEditWorkingTimeDialog}
                onClickArchiveProjectDialog={openArchiveProjectDialog}
                onClickExportPdfForSelectedTime={exportPdfForSelectedTime}
                onClickExportCsv={exportCsv}
                onClickDeleteWorkingTimeDialog={openDeleteWorkingTimeDialog}
            />
            <EditWorkingTimeDialog
                open={editWorkingTimeDialogOpen}
                onClose={closeEditWorkingTimeDialog}
                workingTime={workingTimeList?.find(
                    workingTime => workingTime.workingTimeId === workingTimeToEdit,
                )}
                projectEmployees={project.employees}
                availableTags={availableTags}
            />
            <DeleteWorkingTimeDialog
                open={deleteWorkingTimeDialogOpen}
                onClose={closeDeleteWorkingTimeDialog}
                workingTime={workingTimeList?.find(
                    wt => wt.workingTimeId === workingTimeToDelete,
                )}
            />
            <TrackTimeDialog
                open={trackTimeDialogOpen}
                onClose={closeTrackTimeDialog}
                projectId={project.projectId}
                projectEmployees={project.employees}
                availableTags={availableTags}
            />
            <ArchiveProjectDialog
                open={archiveProjectDialogOpen}
                onClose={closeArchiveProjectDialog}
                projectId={project.projectId}
            />
        </Box>
    );
};

export default ProjectDetailsPage;
